import update from 'immutability-helper';
import { COUNT } from '@utils/metrics';
import * as dimContainerTypes from '@store/dimensionContainer/types';
import * as comparisonTypes from '@store/comparison/types';
import { DEFAULT_METRIC } from '@utils/constants';
import * as types from './types';
import { createDimension } from './schema';

const initialState = {
  dimensions: {},
  dimensionSortByMetric: DEFAULT_METRIC,
  metrics: [DEFAULT_METRIC],
  metricUnit: COUNT,
  selectedDimensionInCompareMode: '',
  highlightAllCompareFilters: false,
  sliceFrom: '',
  sliceTo: '',
  isOrderByTriggered: false,
  dimensionTableHeaderInterval: null,
};

const switchMap = {};

switchMap[types.INIT] = (state) =>
  update(state, {
    $set: initialState,
  });

switchMap[types.SET_INITIAL_STATE] = (state, action) =>
  update(state, {
    $merge: action.state,
  });

switchMap[types.SET_DIMENSIONS] = (state, action) =>
  update(state, {
    dimensions: {
      $set: action.dimensions,
    },
  });

switchMap[types.CHANGE_DIMENSION_SORT] = (state, action) =>
  update(state, {
    dimensions: {
      [action.current.dimensionName]: {
        sort: {
          $set: action.current.sort,
        },
      },
      [action.target.dimensionName]: {
        sort: {
          $set: action.target.sort,
        },
      },
    },
  });

switchMap[types.CHANGE_DIMENSION] = (state, action) =>
  update(state, {
    dimensions: {
      $unset: [action.current.dimensionName],
      [action.target.dimensionName]: {
        $set: createDimension(action.target.dimensionName, action.target.sort),
      },
    },
  });

switchMap[types.SET_METRICS] = (state, action) =>
  update(state, {
    metrics: {
      $set: action.metrics,
    },
  });

switchMap[types.SET_DIMENSION_SORT_BY_METRIC] = (state, action) =>
  update(state, {
    dimensionSortByMetric: {
      $set: action.dimensionSortByMetric,
    },
  });

switchMap[types.SET_METRIC_UNIT] = (state, action) =>
  update(state, {
    metricUnit: {
      $set: action.metricUnit,
    },
  });

switchMap[types.SET_ALL_QUERIES] = (state, action) =>
  update(state, {
    dimensions: {
      $set: Object.keys(state.dimensions).reduce((dimensions, dimensionName) => {
        const mirrorDimensions = dimensions;
        mirrorDimensions[dimensionName] = {
          ...state.dimensions[dimensionName],
          query: action.queries[dimensionName],
          invalid: true,
        };
        return dimensions;
      }, {}),
    },
  });

switchMap[types.SET_QUERY] = (state, action) =>
  update(state, {
    dimensions: {
      [action.dimensionName]: {
        query: {
          $set: action.query,
        },
        invalid: {
          $set: true,
        },
      },
    },
  });

switchMap[types.FETCHING_DIMENSION_DATA] = (state, action) => {
  // race condition when switching to trends from a shared page
  // on mount action of dimension container + reset trends + persistence
  // this is a temp fix to resolve the exception
  try {
    return update(state, {
      dimensions: {
        [action.dimensionName]: {
          isFetching: {
            $set: true,
          },
        },
      },
    });
  } catch (err) {
    // ignore exception
  }
  return state;
};

switchMap[types.FETCHING_DIMENSION_DATA_SUCCESS] = (state, action) =>
  update(state, {
    dimensions: {
      [action.dimensionName]: {
        $merge: {
          response: action.response,
          isFetching: false,
          errorCode: 0,
          errorMsg: '',
          invalid: false,
        },
      },
    },
  });

switchMap[types.FETCHING_DIMENSION_DATA_FAIL] = (state, action) =>
  update(state, {
    dimensions: {
      [action.dimensionName]: {
        $merge: {
          isFetching: false,
          errorCode: action.errorCode || 500,
          errorMsg: action.errorMsg || 'Unknown error.',
          invalid: true,
        },
      },
    },
  });

switchMap[types.FETCHING_DIMENSION_DATA_ABORT] = (state, action) => {
  // This catch block is an temporary fix for CSR-6105
  // When aborting the dimension fetch mid way for an error metric (VSF/VPF)
  // the merge fails as action.dimensionName for ErrorCode would be undefined.
  try {
    return update(state, {
      dimensions: {
        [action.dimensionName]: {
          $merge: {
            isFetching: false,
          },
        },
      },
    });
  } catch (err) {
    // ignore exception
  }
  return state;
};

switchMap[comparisonTypes.MODIFY_COMPARISON] = (state, action) => {
  const { dimensionSortByMetric } = action.config;
  return update(state, {
    dimensionSortByMetric: { $set: dimensionSortByMetric },
  });
};

switchMap[dimContainerTypes.SELECT_MULTIPLE_DIMENSION_IN_COMPARE_DRAWER] = (state, action) =>
  update(state, {
    highlightAllCompareFilters: {
      $set: true,
    },
    selectedDimensionInCompareMode: {
      $set: action.dimensionName,
    },
  });

switchMap[dimContainerTypes.UNSELECT_MULTIPLE_DIMENSION_IN_COMPARE_DRAWER] = (state, action) =>
  update(state, {
    // eslint-disable-line
    highlightAllCompareFilters: {
      $set: false,
    },
    selectedDimensionInCompareMode: {
      $set: '',
    },
  });

switchMap[types.ORDER_BY_TRIGGER_ACTION] = (state, action) =>
  update(state, {
    isOrderByTriggered: {
      $set: action.isOrderByTriggered,
    },
  });

switchMap[types.SET_DIMENSION_TABLE_HEADER] = (state, action) =>
  update(state, {
    dimensionTableHeaderInterval: {
      $set: action.interval,
    },
  });

export default (state = initialState, action) =>
  switchMap[action.type] ? switchMap[action.type](state, action) : state;
